// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import { environment } from '@shared/environment';

export enum AggregatorProvider {
  WealthReader = 'wealth_reader',
}

export const useOAuthCallback = () => {
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const params = useParams<{ provider: string }>();
  const provider = useMemo(() => params.provider, [params.provider]);

  const [createAdvisoryInstitutions] =
    advisoryApi.useCreateAdvisoryInstitutionsMutation();

  const verifyAndSubmitCredentials = useCallback(async () => {
    try {
      const public_token = queryParams.get('code');
      const receivedNonce = queryParams.get('nonce');
      const code_verifier = localStorage.getItem('code_verifier');
      const nonce = localStorage.getItem('nonce');
      const redirect_uri =
        provider === AggregatorProvider.WealthReader
          ? environment.SERVICES.WEALTH_READER_REDIRECT_URL!
          : undefined;

      // Verify nonce to prevent CSRF attacks
      if (nonce !== receivedNonce) {
        setError(
          'This request could not be verified, please try again or contact support.',
        );
        return;
      }

      // Check for required parameters before proceeding
      if (!public_token || !code_verifier) {
        setError('Invalid request, please try again or contact support.');
        return;
      }

      // Create institution using provided credentials
      await createAdvisoryInstitutions({
        provider: provider as AggregatorProvider,
        public_token,
        code_verifier,
        redirect_uri,
      }).unwrap();

      navigate('/banks');
    } catch (err: unknown) {
      const errorString = (err as any)?.data?.detail;
      setError(
        `An error occurred while processing your request: ${
          errorString || 'Unknown error'
        }. Please try again or contact support if the issue persists.`,
      );
    } finally {
      setIsLoading(false);
      localStorage.removeItem('code_verifier');
      localStorage.removeItem('nonce');
    }
  }, [createAdvisoryInstitutions, navigate, provider, queryParams]);

  useEffect(() => {
    if (provider !== AggregatorProvider.WealthReader) {
      setError('Invalid provider, please try again or contact support.');
      setIsLoading(false);
      return;
    }
    verifyAndSubmitCredentials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    error,
    isLoading,
  };
};
